import {
  AgendaSpeaker,
  PublicEventDetailsResponse,
  PublicExhibitionItem,
  PublicSessionItem,
  Portfolio,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Document, Image, Link, Page, Text, View } from '@react-pdf/renderer';
import { useCallback, useState, useEffect, useMemo } from 'react';

import weLogoBlackLove from '@assets/images/made_with_logo_black.png';
import weLogoWhiteLove from '@assets/images/made_with_love_white.png';
import weLogoBlack from '@assets/images/we_conect_logo_black.png';
import weLogoWhite from '@assets/images/we_conect_logo_white.png';
import { adminEventsRouteFormat } from '@constants/paths';
import { useTranslation } from 'react-i18next';
import { DefaultTheme } from 'styled-components';
import { APP_HOST } from '@utils/apiHooks';
import BusinessPartner from './components/BusinessPartner';
import EventAtGlance from './components/EventAtGlance';
import EventDetailsBody from './components/EventDetailsBody';
import Exhibitions from './components/Exhibitions';
import KeyTopics from './components/KeyTopics';
import MeetBusinessLeaders from './components/MeetBusinessLeaders';
import Moderators from './components/Moderators';
import MultiTouchPoint from './components/MultiTouchPoint';
import Speakers from './components/Speakers';
import TargetGroups from './components/TargetGroups';
import { styles } from './styles';
import { getImageSrc } from '@utils/fn';

import TitlePage from './pages/TitlePage';
import AgendaBodyPages from './pages/AgendaBodyPages';
import ContactPage from './pages/ContactPage';
import TestimonialsBookNow from './components/TestimonialsBookNow';
import { Ticket } from '@composites/Tickets';
import AlreadyRegistered from './components/AlreadyRegistered';
import IncreaseYourSales from './pages/IncreaseYourSales';
import ColocatedEvents from './pages/ColocatedEvents';
import MatchMakingOne2OneMeetingsPage from './pages/MatchMakingOne2OneMeetingsPage';
import EventLocation from './components/EventLocation';
import BookNowPage from './pages/BookNowPage';
import IcebreakerDinnerPage from './pages/IcebreakerDinnerPage';
import IntermediatePage from './pages/IntermediatePage';
import { fetchTicketsByPageType } from '@utils/ticketUtils';

interface AgendaPdfContentProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  theme: DefaultTheme;
  groupedAgendasSessions: PublicSessionItem[][];
  agendaPdfDates: string[];
  agendaSpeakers: AgendaSpeaker[];
  exhibitions: PublicExhibitionItem[];
  tickets: Ticket[];
  advertisements: any[] | undefined;
}

const AgendaPdfContent = ({
  event,
  coLocatedEvents,
  portfolios,
  groupedAgendasSessions,
  agendaPdfDates,
  agendaSpeakers,
  exhibitions,
  tickets,
  advertisements,
}: AgendaPdfContentProps) => {
  const { t } = useTranslation();

  const Header = useCallback(
    ({
      event,
      lightMode,
    }: {
      event: PublicEventDetailsResponse;
      lightMode: boolean;
    }) => {
      const eventHomePageUrl = `${APP_HOST}/events/${event?.slug}`;
      const bookNowUrl = `${APP_HOST}/events/${event?.slug}${t('book-now-url')}`;
      const partnerUrl = `${APP_HOST}/events/${event?.slug}${t('partnering-packages-url')}`;

      return (
        <View style={styles.headerWrapper} fixed>
          <Link src={eventHomePageUrl} style={{ textDecoration: 'none' }}>
            <Image
              src={getImageSrc(
                lightMode ? event?.images?.logoTransparent : event?.images?.logo
              )}
              style={styles.eventLogo}
            />
          </Link>

          <View style={styles.buttonsContainer}>
            <Link
              src={bookNowUrl}
              style={{
                ...styles.primaryButton,
                backgroundColor: event?.design?.themeColor || 'black',
              }}
            >
              <Text style={styles.primaryButtonText}>{t('book-now')}</Text>
            </Link>

            <Link
              src={partnerUrl}
              style={{
                ...styles.secondaryButton,
                borderColor: lightMode ? 'white' : 'black',
              }}
            >
              <Text
                style={{
                  ...styles.secondaryButtonText,
                  color: lightMode ? 'white' : 'black',
                }}
              >
                {t('apply-as-partner-pdf')}
              </Text>
            </Link>
          </View>
        </View>
      );
    },
    []
  );

  const Footer = useCallback(
    ({ lightMode }: { lightMode?: boolean }) => {
      const eventLink = `${APP_HOST}${adminEventsRouteFormat(event?.slug, '').slice(0, -1)}
            `;
      return (
        <View
          style={{
            ...styles.footer,
            backgroundColor: lightMode ? 'transparent' : '#EEEEEE',
          }}
          fixed
        >
          <View style={styles.footerLeftItem}>
            <Link
              src={eventLink}
              style={{
                ...styles.eventName,
                color: lightMode
                  ? 'white'
                  : (event?.design?.themeColor ?? 'black'),
                textDecoration: 'none',
              }}
            >
              {t('visit-the-website-here')}
            </Link>
          </View>

          <View style={styles.footerMiddleItem}>
            <Text
              style={{
                ...styles.pageNr,
                color: lightMode ? 'white' : '#27343d',
              }}
              render={({ pageNumber, totalPages }) => {
                if (pageNumber > 1) {
                  return `${t('page')} ${pageNumber}`;
                }
                return '';
              }}
              fixed
            />
            <Text
              style={{
                ...styles.heartIcon,
                color: lightMode ? 'white' : '#27343d',
              }}
            >
              {t('agenda-pdf-footer-text-1')} {'\uf004'}{' '}
              {t('agenda-pdf-footer-text-2')}
            </Text>
          </View>

          <View style={styles.footerRightItem}>
            <Image
              style={styles.logo}
              src={lightMode ? weLogoWhite : weLogoBlack}
            />
          </View>
        </View>
      );
    },
    [event?.design?.themeColor, event?.slug, t]
  );

  const homeTickets = useMemo<Ticket[]>(() => {
    const wc: any = event?.website_content || {};
    const wcTickets = wc?.tickets || [];

    const homeTicketIds = Array.isArray(wcTickets)
      ? wcTickets
          .filter((t: any) => t.page === 'home')
          .map((t: any) => t.ticket)
      : [];

    const filtered = tickets.filter(t => homeTicketIds.includes(t.id));

    const further: Ticket = {
      ...filtered[0],
      id: 'further-event-passes',
      name: t('further-event-passes'),
      price: 0,
    };

    return [...filtered, further];
  }, [event?.website_content, tickets, t]);

  return (
    <Document>
      <TitlePage
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        footer={<Footer lightMode={false} />}
      />

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 100 }}>
        <Header event={event} lightMode={false} />
        <KeyTopics event={event} />
        <Footer lightMode={false} />
      </Page>

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 60 }}>
        <Header event={event} lightMode={false} />
        <MultiTouchPoint event={event} />
        <Footer lightMode={false} />
      </Page>

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 100 }}>
        <Header event={event} lightMode={false} />
        <TargetGroups event={event} />
        <Footer lightMode={false} />
      </Page>

      <Page
        size="A4"
        orientation="portrait"
        style={{ backgroundColor: '#0F91AE', paddingBottom: 100 }}
      >
        <Header event={event} lightMode={true} />
        <AlreadyRegistered event={event} advertisements={advertisements} />
        <Footer lightMode={true} />
      </Page>

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 60 }}>
        <Header event={event} lightMode={false} />
        <TestimonialsBookNow event={event} tickets={homeTickets} />
        <Footer lightMode={false} />
      </Page>

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 100 }}>
        <Header event={event} lightMode={false} />
        <Moderators event={event} agendaSpeakers={agendaSpeakers} />
        <Footer lightMode={false} />
      </Page>

      <AgendaBodyPages
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        agendaPdfDates={agendaPdfDates}
        groupedAgendasSessions={groupedAgendasSessions}
        header={<Header event={event} lightMode={false} />}
        footer={<Footer lightMode={false} />}
      />

      <IntermediatePage
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        header={<Header event={event} lightMode={true} />}
        footer={<Footer lightMode={true} />}
      />

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 60 }}>
        <Header event={event} lightMode={false} />
        <Exhibitions event={event} />
        <Footer lightMode={false} />
      </Page>

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 100 }}>
        <Header event={event} lightMode={false} />
        <BusinessPartner event={event} exhibitions={exhibitions} />
        <Footer lightMode={false} />
      </Page>

      <IncreaseYourSales
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        footer={<Footer lightMode={false} />}
        header={<Header event={event} lightMode={true} />}
      />

      {event?.colocated && (
        <ColocatedEvents
          event={event}
          coLocatedEvents={coLocatedEvents}
          portfolios={portfolios}
          footer={<Footer lightMode={false} />}
          header={<Header event={event} lightMode={true} />}
        />
      )}

      <MatchMakingOne2OneMeetingsPage
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        footer={<Footer lightMode={false} />}
        header={<Header event={event} lightMode={false} />}
      />

      <Page size="A4" orientation="portrait" style={{ paddingBottom: 100 }}>
        <Header event={event} lightMode={false} />
        <EventLocation event={event} />
        <Footer lightMode={false} />
      </Page>

      <IcebreakerDinnerPage
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        footer={<Footer lightMode={false} />}
        header={<Header event={event} lightMode={true} />}
      />

      <BookNowPage
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        footer={<Footer lightMode={false} />}
        header={<Header event={event} lightMode={false} />}
      />

      <ContactPage
        event={event}
        coLocatedEvents={coLocatedEvents}
        portfolios={portfolios}
        footer={<Footer lightMode={false} />}
        header={<Header event={event} lightMode={true} />}
      />
    </Document>
  );
};

export default AgendaPdfContent;
